
import Vue, { PropType } from 'vue';
import { CvFileUploader } from '@carbon/vue';
import { VFileUploader, VFileUploaderFile } from '@/components/w2pFileUploader';
import { supportedExtensions } from '@/constants/supportedExtensions';

export default Vue.extend({
  components: { CvFileUploader },
  inheritAttrs: false,
  props: {
    validateFn: {
      type: Function as PropType<(props: Record<string, unknown>) => true>,
      required: true,
    },

    helperText: {
      type: String,
      default: `
          You can specify a file to import by either dragging it into the drag and
          drop area or by selecting a file from your computer. Maximum file size of
          50MB (.pdf files only)
      `,
    },
  },

  data() {
    return {
      /**
       * Reference to CvFileUploader component
       */
      fileUploaderRef: null as unknown,
      files: [] as unknown as VFileUploaderFile[],
      supportedExtensions,
      maxFileSize: 50,
    };
  },

  computed: {
    isEmptyInternalFiles(): boolean {
      return (
        (this.fileUploaderRef as VFileUploader)?.internalFiles.length === 0
      );
    },
    /**
     * This will be the flag, for file-uploader--disabled class
     * to appear, so that we can scope disabled styles in this state
     */
    isDisabled(): boolean {
      return this.$attrs.disabled as unknown as boolean;
    },
  },

  mounted() {
    (this.fileUploaderRef as VFileUploader) = this.$refs
      ?.fileUploader as VFileUploader;
  },

  methods: {
    async upload(files: VFileUploaderFile[]) {
      const uploadResults = [];

      for (const file of files) {
        /**
         * Safe check, in case when no file is present
         */
        if (!file) {
          this.$emit('change', files);
          return;
        }

        this.setState(0, 'uploading');
        this.$emit('upload:init');

        try {
          const result = {
            pngFile: await this.getBase64(file.file),
            fileName: file.file.name,
          };
          this.setState(0, 'complete');
          this.$emit('upload:success', result);

          uploadResults.push(result);
        } catch (e) {
          this.setState(0, '');
          this.setInvalidMessage(
            0,
            this.$t(
              'upload-logo.inline-notification.something-went-wrong'
            ).toString()
          );
          this.$emit('upload:error', e);
          throw e;
        }
      }

      return uploadResults;
    },

    /**
     * Wrapper around CvFileUploader internal method
     */
    setState(idx: number, state = '') {
      !this.isEmptyInternalFiles &&
        (this.fileUploaderRef as VFileUploader).setState(idx, state);
    },

    /**
     * Wrapper around CvFileUploader internal method
     */
    setInvalidMessage(idx: number, msg: string) {
      !this.isEmptyInternalFiles &&
        (this.fileUploaderRef as VFileUploader).setInvalidMessage(idx, msg);
    },

    /**
     * Manipulate uploading state here, $emit events
     */
    async forwardOnChange(files: VFileUploaderFile[]) {
      /**
       * In case of a drag & drop, replace previously dropped in file
       */
      // if (files.length > 1) {
      //   (this.fileUploaderRef as VFileUploader).remove(0);
      //   return;
      // }
      this.$emit('change', files);

      for (const file of files) {
        /**
         * Return early, in case of a file removal
         */
        if (!file) {
          return;
        }

        /**
         * In case we have a file, we proceed with basic validationObj on type & size
         */
        const { size, type } = file.file;
        const validationObj = this.validateFn({
          type: type,
          size,
          maxFileSize: this.maxFileSize,
          supportedExtensions: this.supportedExtensions,
        });
        const errorAny = Object.values(validationObj).some(
          ({ valid }) => !valid
        );

        if (errorAny) {
          let error = Object.values(validationObj).find(
            ({ valid }: { valid: boolean }) => !valid
          );

          if (error.errorMsg === 'invalid-file-size') {
            this.setInvalidMessage(
              0,
              this.$tc(
                `upload-logo.inline-notification.${error.errorMsg}`,
                error.value,
                { value: error.value }
              ).toString()
            );
          }
          if (error.errorMsg === 'invalid-file-type') {
            this.setInvalidMessage(
              0,
              this.$t(
                `upload-logo.inline-notification.${error.errorMsg}`
              ).toString()
            );
          }

          this.setState(0, '');
          this.$emit('upload:validation-error');
          return;
        }
      }

      /**
       * In case everything is fine, we proceed with file upload
       * when apiOptions.lazy flag isn't being set
       */
      this.files = files; // we need this for scoped slot's usage

      /**
       * Upload immedately if no apiOption.lazy is set to true
       * -> otherwise expose files, and upload() method externally
       * so that consumer can defer a call to this method
       */

      this.$emit('upload:ready', { files, up: this.upload });
    },
    getBase64(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          let encoded = reader?.result
            ?.toString()
            .replace(/^data:(.*,)?/, '') as string;
          if (encoded.length % 4 > 0) {
            encoded += '='.repeat(4 - (encoded.length % 4));
          }
          resolve(encoded);
        };
        reader.onerror = (error) => reject(error);
      });
    },
  },
});
